import React, { useState } from 'react';
import { pick, cloneDeep } from 'lodash';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, ButtonGroup } from '@mui/material';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { CustomTreeItem } from './JsonTreeView.style';
import { CopyJsonButton } from './CopyJsonButton';

const LOCK_FILES_LIST = [
  'package-lock.json',
  'yarn.lock',
  'npm-shrinkwrap.json',
  'Pipfile.lock',
  'poetry.lock',
  'conda-lock.yml',
  'Gemfile.lock',
  'composer.lock',
  'packages.lock.json',
  'project.assets.json',
  'pom.xml',
  'Cargo.lock',
  'mix.lock',
  'pubspec.lock',
  'go.sum',
  'stack.yaml.lock',
  'vcpkg.json',
  'conan.lock',
  'ivy.xml',
  'project.clj',
  'Podfile.lock',
  'Cartfile.resolved',
  'flake.lock',
  'pnpm-lock.yaml'
];

const EXCLUDE_EXPRESSIONS_LIST = [
  '.*\\.(ini|csv|xls|xlsx|xlr|doc|docx|txt|pps|ppt|pptx|dot|dotx|log|tar|rtf|dat|ipynb|po|profile|object|obj|dxf|twb|bcsymbolmap|tfstate|pdf|rbi|pem|crt|svg|png|jpeg|jpg|ttf|app|bin|bmp|bz2|class|db|dll|dylib|egg|eot|exe|gif|gitignore|glif|gradle|gz|ico|jar|lo|lock|mp3|mp4|nar|o|ogg|otf|p|pickle|pkl|pyc|pyd|pyo|rkt|so|ss|tgz|tsv|war|webm|woff|woff2|xz|zip|zst|snap|lockb)$',
  '.*(yarn|gemfile|podfile|cargo|composer|pipfile|gopkg)\\.lock$',
  '.*gradle\\.lockfile$',
  '.*lock\\.sbt$',
  '.*dist/.*\\.js',
  '.*build/.*\\.js',
  '.*public/assets/.*\\.js'
];
const IGNORE_FILES_REGEX_LIST = [
  ...LOCK_FILES_LIST.map(f => f.replace('.', '\\.')),
  ...EXCLUDE_EXPRESSIONS_LIST
];
const EXCLUDE_PATTERN = new RegExp(IGNORE_FILES_REGEX_LIST.join('|'));

const shouldExcludeFile = file => {
  const shouldExludeByName =
    EXCLUDE_PATTERN.test(file.original_file) ||
    EXCLUDE_PATTERN.test(file.new_file);

  return shouldExludeByName;
};

const createSubTree = (currentPath, index, key, node) => {
  return (
    <CustomTreeItem key={index} nodeId={currentPath} label={key}>
      {renderTree(node, `${index}`, currentPath)}
    </CustomTreeItem>
  );
};

export const renderTree = (nodes, index, path = '') => {
  if (
    typeof nodes === 'string' ||
    typeof nodes === 'number' ||
    typeof nodes === 'boolean'
  ) {
    return (
      <CustomTreeItem key={index} nodeId={index} label={nodes.toString()} />
    );
  }

  if (Array.isArray(nodes)) {
    return nodes.map((node, index) => {
      const currentPath = `${path}[${index}]`;

      return (
        <CustomTreeItem
          key={index}
          nodeId={currentPath}
          label={node.toString()}
        >
          {renderTree(node, `${index}`, currentPath)}
        </CustomTreeItem>
      );
    });
  }

  return Object.keys(nodes || {}).map((key, index) => {
    const currentPath = path ? `${path}.${key}` : key;
    return createSubTree(currentPath, index, key, nodes[key]);
  });
};

/**
 * Cleans the pull request description by removing placeholder description that generated by gitstream AI
 * @param description full pr description
 * @returns cleaned description
 */
const cleanPrDescription = description => {
  // remove workerB badge if present
  const workerBBadgePattern =
    /\[!\[workerB\]\(https:\/\/img\.shields\.io\/endpoint\?url=.*?\)\]\(https?:\/\/.*?\/v2\/badge\/collaboration-page\?magicLinkId=.*?\)/g;
  const cleanedDescription = description?.replace(workerBBadgePattern, '');
  const githubRegexPattern =
    /(?:\n|\r\n)?<!--start_gitstream_placeholder-->\s*([\s\S]*?)\s*<!--end_gitstream_placeholder-->(?:\n|\r\n)?/g;

  return cleanedDescription?.replace(githubRegexPattern, '').trim();
};

const extractGitStreamReviews = (prComments = [], prReviews = []) => {
  const reviewPrefix = '### ✨ PR Review';
  const previousGitStreamReviews = [];

  if (prComments.length) {
    const filteredComments = prComments.filter(comment =>
      comment.content.includes(reviewPrefix)
    );
    previousGitStreamReviews.push(...filteredComments);
  }

  if (prReviews.length) {
    const filteredReviews = prReviews.filter(review =>
      review.content.includes(reviewPrefix)
    );
    previousGitStreamReviews.push(...filteredReviews);
  }

  return previousGitStreamReviews;
};

export const createGitstreamAIPrContext = context => {
  if (!context || Object.keys(context).length === 0) {
    return { value: 'No context' };
  }

  const branch = pick(context.branch, ['name', 'diff', 'commits']);
  const pr = pick(context.pr, ['title', 'description', 'labels', 'url']);
  const repo = pick(context.repo, ['languages']);
  const comments = context.pr?.comments?.filter(comment =>
    comment?.content?.includes('### ✨ PR Review')
  );
  pr.previous_gitstream_reviews = comments;
  pr.description = cleanPrDescription(pr.description || '');
  repo.url = context.pr?.url?.split('/pull/')[0] || '';

  const prComments = context.pr.comments || [];
  const prReviews = context.pr.reviews || [];
  const previousGitStreamReviews = extractGitStreamReviews(
    prComments,
    prReviews
  );

  pr.previous_gitstream_reviews = previousGitStreamReviews;
  pr.comments = [];
  pr.reviews = [];

  const clonedSource = cloneDeep(context.source);
  clonedSource.diff.files = clonedSource.diff.files.filter(
    file => !shouldExcludeFile(file)
  );

  return {
    branch,
    source: clonedSource,
    repo,
    files: context.files || [],
    pr
  };
};

const JsonTreeView = ({ context }) => {
  const [expanded, setExpanded] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const prContext = createGitstreamAIPrContext(context);

  const handleToggleClick = () => {
    if (isExpanded) {
      setExpanded([]);
    } else {
      const allNodeIds = getAllNodeIds(context);
      setExpanded(allNodeIds);
    }
    setIsExpanded(!isExpanded);
  };

  const getAllNodeIds = (nodes, path = '') => {
    if (
      typeof nodes === 'string' ||
      typeof nodes === 'number' ||
      typeof nodes === 'boolean'
    ) {
      return [path];
    }

    if (Array.isArray(nodes)) {
      return nodes.flatMap((node, index) => {
        const currentPath = `${path}[${index}]`;
        return [currentPath, ...getAllNodeIds(node, currentPath)];
      });
    }

    return Object.keys(nodes || {}).flatMap(key => {
      const currentPath = path ? `${path}.${key}` : key;
      return [currentPath, ...getAllNodeIds(nodes[key], currentPath)];
    });
  };

  return (
    <Box>
      <Box display='flex' justifyContent='flex-end' mb={2}>
        <ButtonGroup variant='outlined' size='small'>
          <CopyJsonButton
            context={prContext}
            text='Copy PR Context'
            title='Copy all gitstream AI PR context'
          />
          <Button onClick={handleToggleClick}>
            {isExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
          <CopyJsonButton
            context={context}
            text='Copy JSON'
            title='Copy all context data'
          />
        </ButtonGroup>
      </Box>

      <TreeView
        expanded={expanded}
        onNodeToggle={(event, nodeIds) => setExpanded(nodeIds)}
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
      >
        {renderTree(context, '0')}
      </TreeView>
    </Box>
  );
};

export default JsonTreeView;
